<template>
  <section class="flex flex-1 min-w-0 flex-col h-full min-h-0 bg-white overflow-hidden"
    :class="flush ? '' : 'border border-gray-200 rounded-xl'"
    data-testid="ai-workspace">
    <!-- Header: user-controlled stack vs replace + clear all -->
    <header v-if="showControls" class="flex items-center gap-2 px-3 py-2 border-b border-gray-100 shrink-0">
      <span class="text-xs font-medium text-gray-500 mr-auto">Workspace</span>

      <!-- Paul: stack vs replace "should be determined by the user for now" -->
      <div class="inline-flex rounded-lg border border-gray-200 overflow-hidden" role="group"
        aria-label="How new cards are added">
        <button type="button"
          class="px-2.5 py-1 text-xs transition-colors"
          :class="mode === 'stack' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'"
          :aria-pressed="mode === 'stack'"
          data-testid="ai-workspace-mode-stack"
          @click="emit('update:mode', 'stack')">
          Keep cards
        </button>
        <button type="button"
          class="px-2.5 py-1 text-xs transition-colors border-l border-gray-200"
          :class="mode === 'replace' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'"
          :aria-pressed="mode === 'replace'"
          data-testid="ai-workspace-mode-replace"
          @click="emit('update:mode', 'replace')">
          Replace
        </button>
      </div>

      <button v-if="cards.length" type="button"
        class="px-2 py-1 text-xs text-gray-500 hover:text-red-600 rounded transition-colors"
        data-testid="ai-workspace-clear"
        @click="emit('clear')">
        Clear all
      </button>
    </header>

    <!--
      PHASE-2 CARD SYSTEM.
      Cards are chosen by the MODEL (never auto-mapped from tools) and carry only
      a type + ids; each renderer fetches its own data under the signed-in user's
      permissions. Register a new type in ai-cards/aiCardRegistry.ts — nothing
      else in the page needs to change.
    -->
    <div v-if="cards.length" class="flex-1 min-h-0 overflow-y-auto p-3 space-y-3" data-testid="ai-workspace-content">
      <AiWorkspaceCard v-for="card in cards" :key="card.id" :card="card"
        @dismiss="emit('dismiss', $event)"
        @action="emit('action', $event)" />
      <slot />
    </div>

    <!-- Empty state: no cards to show yet -->
    <div v-else class="flex-1 flex flex-col items-center justify-center text-center p-8"
      data-testid="ai-workspace-empty">
      <i class="pi pi-objects-column text-4xl text-gray-300 mb-4"></i>
      <p class="text-gray-600 font-medium mb-1">{{ title }}</p>
      <p class="text-sm text-gray-400 max-w-sm">{{ subtitle }}</p>
    </div>
  </section>
</template>

<script setup lang="ts">
import AiWorkspaceCard from '@/components/common/ai-cards/AiWorkspaceCard.vue';
import type { AiActionOutcome } from '@/components/common/ai-cards/aiCardActions';
import type { AiWorkspaceCardData, AiWorkspaceMode } from '@/composables/useAiChat';

/**
 * The workspace: the components the model chose to display, newest first,
 * each with a title and dismiss control. The header carries the user's
 * stack-vs-replace preference and a Clear all.
 *
 * Two hosts: the full page renders it beside the docked chat; the floating
 * widget renders it `flush` (no outer border/radius) as its Workspace view.
 */
const props = withDefaults(
  defineProps<{
    cards?: AiWorkspaceCardData[];
    mode?: AiWorkspaceMode;
    /** Hide the header entirely (e.g. a bare placeholder usage). */
    showControls?: boolean;
    /** Drop the outer border/radius when nested inside another panel. */
    flush?: boolean;
    title?: string;
    subtitle?: string;
  }>(),
  {
    cards: () => [],
    mode: 'stack',
    showControls: true,
    flush: false,
    title: 'Ask the assistant and results will appear here',
    subtitle:
      'Student details, billing and results the assistant decides to show will appear in this space.',
  },
);

const emit = defineEmits<{
  (e: 'update:mode', mode: AiWorkspaceMode): void;
  (e: 'dismiss', id: string): void;
  (e: 'clear'): void;
  (e: 'action', outcome: AiActionOutcome): void;
}>();

const {
  title, subtitle, cards, mode, showControls, flush,
} = toRefs(props);
</script>
